import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { useApi } from "../utils/Api";
import OrderHistory from "../components/OrderHistory";
import ConfirmationModal from "../components/ConfirmationModal";

/* ─── tiny helpers ─── */
const initials = (u) =>
  `${u?.first_name?.[0] || ""}${u?.last_name?.[0] || ""}`.toUpperCase() || "?";

const Field = ({ label, icon, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#999]">
      <i className={`${icon} text-[#fa9324]`} />
      {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "w-full rounded-xl border border-[#333] bg-[#1a1a1a] px-4 py-3 text-sm text-white placeholder-[#555] outline-none transition focus:border-[#fa9324] focus:bg-[#1f1f1f] focus:ring-2 focus:ring-[#fa9324]/25";

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUser, signOut, userLoading } = useUser();
  const { apiFetch } = useApi();

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState(
    searchParams.get("tab") || "loyalty"
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  /* redirect guard */
  useEffect(() => {
    if (!userLoading && !user) navigate("/", { replace: true });
  }, [user, userLoading, navigate]);

  /* sync activeTab with URL tab param */
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "orders") {
      setActiveTab("orders");
    } else if (tab === "profile") {
      setActiveTab("profile");
    } else if (tab === "loyalty") {
      setActiveTab("loyalty");
    } else {
      setActiveTab("loyalty");
    }
  }, [searchParams]);

  /* populate fields */
  useEffect(() => {
    if (user) {
      setFirstName(user?.first_name || "");
      setLastName(user?.last_name || "");
      setEmail(user?.email || "");
      setPhone(user?.phone || "");
    }
  }, [user]);

  /* API sync on mount */
  useEffect(() => {
    if (!user?.id) return;
    apiFetch("get-customer-profile", { customer_id: user.id, _t: Date.now() })
      .then((data) => {
        console.log("[ProfilePage] get-customer-profile response:", data);
        const profileData = data.user || data.customer || data.data;
        if (data.success && profileData) {
          // Use functional updater to avoid stale closure
          setUser((prev) => ({ ...prev, ...profileData }));
        }
      })
      .catch((err) => console.error("Profile sync failed:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleSave = async () => {
    if (!user?.id) return;
    const fn = firstName.trim(), ln = lastName.trim();
    const em = email.trim(), ph = String(phone ?? "").trim();

    if (!fn || !ln || !em || !ph) { setError("All fields are required."); return; }
    if (!/^\S+@\S+\.\S+$/.test(em)) { setError("Please enter a valid email address."); return; }

    setLoading(true); setError(""); setSuccessMsg("");
    try {
      const data = await apiFetch("update-profile", {
        user_id: user.id, first_name: fn, last_name: ln, phone: ph, email: em,
      });
      if (data.status === "success") {
        // Use the number as the server saved it (standard +44… form).
        const savedPhone = data.customer?.phone ?? ph;
        setPhone(savedPhone);
        setUser({ ...user, ...data.customer, first_name: fn, last_name: ln, phone: savedPhone, email: em });
        setSuccessMsg("Profile updated successfully!");
        setTimeout(() => setSuccessMsg(""), 3500);
      } else {
        if (data.message?.includes("Phone")) setError("Phone number is already in use.");
        else if (data.message?.includes("Email")) setError("Email is already in use.");
        else setError(data.message || "Failed to update profile.");
      }
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { signOut(); navigate("/", { replace: true }); };

  const sectionRef = React.useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (sectionRef.current) {
        const topOffset = window.innerWidth < 1024 ? 90 : 110; 
        const elementPosition = sectionRef.current.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: elementPosition - topOffset,
          behavior: 'smooth'
        });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const [todayEarnedPoints, setTodayEarnedPoints] = useState(0);
  const [isLoadingPoints, setIsLoadingPoints] = useState(true);

  // Fetch today's earned points to calculate pending balance
  useEffect(() => {
    if (!user?.id) return;
    let isMounted = true;
    setIsLoadingPoints(true);
    const fetchTodayPoints = async () => {
      try {
        const data = await apiFetch("get-customer-orders", { customer_id: user.id });
        if (isMounted && data.status === "success" && Array.isArray(data.orders)) {
          // 'en-CA' locale formats date as YYYY-MM-DD which matches our substring extraction
          const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
          let todayPoints = 0;
          data.orders.forEach(order => {
             if (order.created_at) {
               // order.created_at from backend is already in Europe/London time (e.g. "2026-06-06 23:30:00")
               // Parsing it with new Date() would assume local browser time and shift it incorrectly
               const orderDateStr = order.created_at.substring(0, 10);
               if (orderDateStr === todayStr) {
                   todayPoints += Number(order.loyalty_points_earned || 0);
               }
             }
          });
          setTodayEarnedPoints(todayPoints);
        }
      } catch (err) {
        console.error("Failed to fetch orders for loyalty points:", err);
      } finally {
        if (isMounted) setIsLoadingPoints(false);
      }
    };
    fetchTodayPoints();
    return () => { isMounted = false; };
  }, [user?.id, apiFetch]);

  if (!user && !userLoading) return null;

  const loyaltyPoints   = Number(user?.loyalty_points || 0);
  const usablePoints    = Math.max(0, loyaltyPoints - todayEarnedPoints);
  const totalEarned     = Number(user?.loyalty_total_earned || 0);
  const totalUsed       = Number(user?.loyalty_total_used || 0);
  const displayName     = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();

  const TABS = [
    { id: "loyalty", label: "Loyalty Points", icon: "fas fa-coins" },
    { id: "profile", label: "Profile Details", icon: "fas fa-user" },
    { id: "orders",  label: "Order History", icon: "fas fa-receipt" },
  ];

  return (
    <main className="min-h-screen bg-[#0a0a0a] text-white">

      {/* ── Hero / header band ── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0a0a0a] via-[#111] to-[#1a1000] pt-[130px] max-lg:pt-[100px] max-[480px]:pt-[85px] pb-32">
        {/* decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#fa9324]/10 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 w-[300px] h-[300px] rounded-full bg-[#fa9324]/5 blur-[100px]" />

        <div className="relative mx-auto max-w-[1400px] px-[110px] max-lg:px-[50px] max-md:px-[5%]">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">

            {/* Avatar */}
            <div className="relative shrink-0 w-20 h-20">
              <div className="w-full h-full rounded-3xl bg-gradient-to-br from-[#fa9324] to-[#e07010] flex items-center justify-center shadow-[0_8px_32px_rgba(250,147,36,0.35)]">
                <span className="text-3xl font-black text-black font-['Prata',serif]">
                  {initials(user)}
                </span>
              </div>
              {/* online dot */}
              <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#0a0a0a] shadow" />
            </div>

            {/* Name + meta */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-black font-['Prata',serif] text-white leading-tight truncate">
                {displayName || "Your Account"}
              </h1>
              
              <div className="mt-1.5 space-y-1">
                <p className="text-[#aaa] text-xs sm:text-sm flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="fas fa-phone-alt text-[#fa9324] text-[10px]" />
                    {user?.phone || "—"}
                  </span>
                  <span className="text-[#333]">|</span>
                  <span className="flex items-center gap-1.5">
                    <i className="fas fa-envelope text-[#fa9324] text-[10px]" />
                    {user?.email || "—"}
                  </span>
                </p>
                <p className="text-[#aaa] text-xs sm:text-sm flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <i className="fas fa-calendar-alt text-[#fa9324] text-[10px]" />
                    Active since {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-GB", { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}
                  </span>
                  <span className="text-[#333]">|</span>
                  <span className="flex items-center gap-1.5">
                    <i className="fas fa-shopping-bag text-[#fa9324] text-[10px]" />
                    {user?.total_orders || 0} Total Orders
                  </span>
                </p>
              </div>

              {/* Loyalty badge removed for points system */}
            </div>

            {/* Sign Out Button */}
            <div className="shrink-0">
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-6 py-3 text-sm font-black text-white transition hover:bg-red-700 active:scale-[.98] shadow-lg shadow-red-900/20"
              >
                <i className="fas fa-sign-out-alt text-xs" /> Sign Out
              </button>
            </div>
          </div>

          {/* Stat cards and loyalty info removed from hero to reduce scrolling and moved inside the Loyalty Points tab */}
        </div>
      </div>

      {/* ── Main card panel ── */}
      <div ref={sectionRef} className="relative mx-auto max-w-[1400px] px-[110px] max-lg:px-[50px] max-md:px-[5%] -mt-16 pb-20">
        <div className="rounded-3xl border border-[#222] bg-[#111] shadow-[0_24px_60px_rgba(0,0,0,0.6)] overflow-hidden">

          {/* Tab bar */}
          <div className="flex border-b border-[#222] px-4 sm:px-6 pt-1 overflow-x-auto scrollbar-hide whitespace-nowrap flex-nowrap scroll-smooth">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setActiveTab(t.id);
                  navigate(`/profile?tab=${t.id}`, { replace: true });
                }}
                className={`relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-4 text-xs sm:text-sm font-semibold transition-colors shrink-0 ${
                  activeTab === t.id ? "text-[#fa9324]" : "text-[#888] hover:text-[#ccc]"
                }`}
              >
                <i className={`${t.icon} text-xs`} />
                {t.label}
                {activeTab === t.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-t-full bg-[#fa9324]" />
                )}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="p-4 sm:p-6 min-h-[300px]">
            {userLoading || isLoadingPoints ? (
              <div className="flex h-[250px] items-center justify-center flex-col gap-4">
                <div className="w-10 h-10 border-4 border-[#fa9324]/30 border-t-[#fa9324] rounded-full animate-spin" />
                <p className="text-sm text-[#888]">Loading your profile data...</p>
              </div>
            ) : (
              <>
                {/* ── LOYALTY TAB ── */}
                {activeTab === "loyalty" && (
                  <div className="max-w-3xl mx-auto space-y-6">
                    <div>
                      <h2 className="text-xl font-bold font-['Prata',serif] text-white mb-1">
                        Loyalty Points & Rewards
                      </h2>
                      <p className="text-sm text-[#888]">Track your rewards, points earned, and cashback history.</p>
                    </div>

                    {/* Stat cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {[
                        { icon: "fas fa-wallet",        label: "Usable Balance", value: `£${usablePoints.toFixed(2)}`, color: "text-[#fa9324]", border: "border-[#fa9324]/20", subtitle: todayEarnedPoints > 0 ? `+ £${todayEarnedPoints.toFixed(2)} pending` : null },
                        { icon: "fas fa-hand-holding-usd", label: "Total Points Used", value: `£${totalUsed.toFixed(2)}`, color: "text-rose-400", border: "border-rose-500/20" },
                        { icon: "fas fa-chart-line",    label: "Total Points Earned", value: `£${totalEarned.toFixed(2)}`, color: "text-emerald-400", border: "border-emerald-500/20" },
                      ].map(({ icon, label, value, color, border, subtitle }) => (
                        <div key={label} className={`rounded-2xl border ${border} bg-[#161616] p-4 transition-all duration-300 hover:bg-[#1c1c1c]`}>
                          <i className={`${icon} ${color} text-lg mb-2 block`} />
                          <p className="text-[11px] text-[#777] uppercase tracking-wider font-semibold mb-0.5">{label}</p>
                          <p className="text-xl font-black text-white">{value}</p>
                          {subtitle && <p className="text-[10px] text-[#fa9324] mt-1">{subtitle}</p>}
                        </div>
                      ))}
                    </div>

                    {/* Loyalty Info banner */}
                    <div className="rounded-2xl border border-[#fa9324]/20 bg-gradient-to-r from-[#fa9324]/5 to-transparent p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#fa9324]/10 flex items-center justify-center shrink-0">
                        <i className="fas fa-coins text-[#fa9324] text-xl" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">Earn as you eat!</p>
                        <p className="text-xs text-[#888]">
                          Get <span className="text-[#fa9324] font-bold">2% back</span> in points on every order. 
                          Redeem your balance for instant discounts at checkout.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── PROFILE TAB ── */}
                {activeTab === "profile" && (
                  <div className="max-w-3xl mx-auto">
                    {/* Left: form */}
                    <div className="space-y-6">
                      <div>
                        <h2 className="text-xl font-bold font-['Prata',serif] text-white mb-1">
                          Personal Details
                        </h2>
                        <p className="text-sm text-[#888]">Update your name, contact number and email address.</p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="First Name" icon="fas fa-user">
                          <input
                            type="text" value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className={inputCls} placeholder="First name"
                          />
                        </Field>
                        <Field label="Last Name" icon="fas fa-user">
                          <input
                            type="text" value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className={inputCls} placeholder="Last name"
                          />
                        </Field>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Phone Number" icon="fas fa-phone-alt">
                          <input
                            type="tel" inputMode="numeric" value={phone}
                            onChange={(e) =>
                              // digits only (plus a single leading +)
                              setPhone(
                                e.target.value
                                  .replace(/[^\d+]/g, "")
                                  .replace(/(?!^)\+/g, ""),
                              )
                            }
                            maxLength={16}
                            className={inputCls} placeholder="e.g. 07700900000"
                          />
                        </Field>
                        <Field label="Email Address" icon="fas fa-envelope">
                          <input
                            type="email" value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={inputCls} placeholder="you@example.com"
                          />
                        </Field>
                      </div>

                      {/* Feedback */}
                      {error && (
                        <div className="flex items-center gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-400">
                          <i className="fas fa-exclamation-circle" /> {error}
                        </div>
                      )}
                      {successMsg && (
                        <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-400">
                          <i className="fas fa-check-circle" /> {successMsg}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex pt-2">
                        <button
                          type="button" onClick={handleSave} 
                          disabled={loading || (
                            firstName === (user?.first_name || "") &&
                            lastName === (user?.last_name || "") &&
                            phone === (user?.phone || "") &&
                            email === (user?.email || "")
                          )}
                          className="w-full sm:w-max min-w-[200px] inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#fa9324] to-[#e07010] px-8 py-4 font-bold text-black shadow-[0_4px_16px_rgba(250,147,36,0.3)] transition hover:opacity-90 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale-[0.5]"
                        >
                          {loading ? (
                            <><span className="w-4 h-4 border-2 border-black/40 border-t-black rounded-full animate-spin" /> Saving...</>
                          ) : (
                            <><i className="fas fa-save" /> Save Changes</>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── ORDERS TAB ── */}
                {activeTab === "orders" && (
                  <div>
                    <div className="mb-6">
                      <h2 className="text-lg font-bold font-['Prata',serif] text-white mb-1">Order History</h2>
                      <p className="text-sm text-[#888]">View, reorder, or print receipts for your past orders.</p>
                    </div>
                    <div className="pt-2">
                      <OrderHistory user={user} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Logout confirmation modal */}
      <ConfirmationModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="Sign Out"
        message="Are you sure you want to sign out of your account?"
        confirmText="Yes, Sign Out"
      />
    </main>
  );
}